import {
  ContentType,
  SimpleFile,
  Youtube
} from "../../../../domain/entities/content/content";
import { SearchForm } from "../../../../domain/entities/content/search_form";

export type Content = {
  id: number;
  title: string;
  /** Arbitrary total duration. It does not represent the real time the user takes to finish the content, is just an estimated time.*/
  duration: number;
  type: ContentType;
  description: string;
  isBlocked: boolean;
  isReleased: boolean;
  recommendOnDesktop: boolean;
  youtube: Youtube | null;
  exercise: ExerciseStudent | null;
  file: SimpleFile | null;
  isActive: boolean;
  lastChange: string;
  progressStatus: number;
  showMessage: boolean;
  /** The relative time the user must stay on the content screen to set the content as finished. */
  minDuration: number;
  startedAt: string;
  isNew: boolean;
  dCoins: number;
  status: ContentStatus;
  searchForm: SearchForm;
};

export type ExerciseStudent = {
  id: number;

  //IsRequired indicates if the exercise is required answer.
  isRequired: boolean;

  //CodeShowAnswers Field to know if and how will show the answers to student when he finished.
  codeShowAnswers: number;

  //IsAbleToRedoQuestions Field that tells whether the user is able to redo the question.
  isAbleToRedoQuestions: boolean;

  //NumberOfQuestions Is the number of questions that will show to student
  //If this number is less than the length of Question, will list randomly to student.
  numberOfQuestions: number;

  //MinCorrectAnswers The minimum quantity to mark the exercise as complete.
  minCorrectAnswers: number;

  //NeedsAchieveMinAnswers If it is true, the student need answer correctly the minimum, if it is false the student
  //just will know if he would pass or not.
  needsAchieveMinAnswers: boolean;

  //RandomQuestions Randomize the questions when show to student.
  randomQuestions: boolean;

  //AttemptLimit Is how many times the user can redo the exercise.
  attemptLimit: null | number;

  //CompletedAttempts Is how many times the user redo the exercise.
  completedAttempts: number;

  //MaximumTime Is how much time the user have to finish de exercise.
  maximumTime: null | number;

  //Status contains the user status with the exercise.
  status: ExerciseStatus;
};

export type ExerciseStatus = {
  doing: boolean;
  finished: boolean;
  userAttempts: number;
  questionnaire: QuestionnaireStatus;
};

export type QuestionnaireStatus = {
  id: number;
  createdAt: string | null;
  finishedAt: string | null;
  inCorrection: boolean;
  questions: QuestionnaireQuestionsStatus;
};

export type QuestionnaireQuestionsStatus = {
  answered: number;
  corrects: number;
};

export type ContentStatus = {
  progress: number;
  startedAt: string | null;
  finishedAt: string | null;
  scormCheckpoints: ScormCheckpoint[] | null;
  secondsCheckpoint: SecondsCheckpoint | null;
};

export class ScormCheckpoint {
  readonly key: string;
  readonly value: string;

  constructor(key: string, value: string) {
    this.key = key;
    this.value = value;
  }
}

export type SecondsCheckpoint = {
  timestamp: number;
};

export type ContentPosition = number | string;

export class ContentAnnotation {
  public id: number;
  public text: string;
  public contentTimestamp: number | undefined;

  constructor(text: string, timestamp?: number) {
    this.text = text;
    if (timestamp) {
      this.contentTimestamp = timestamp;
    }
    this.id = 0;
  }
}

export type ContentComplement = {
  name: string;
  url: string;
  type: number;
};

export type ContentDoubtAnswer = {
  id: number;
  userName: string;
  userImage: string;
  description: string;
  createdAt: string;
};

export class ContentDoubt {
  readonly id: number;
  title: string;
  description: string;
  readonly createdAt: string;
  readonly userName: string;
  readonly userImage: string | null;
  readonly answer: ContentDoubtAnswer | null;

  constructor(title, description) {
    this.id = 0;
    this.title = title;
    this.description = description;
    this.createdAt = "";
    this.userName = "";
    this.userImage = null;
    this.answer = null;
  }
}

export enum QuestionType {
  Alternative = 1,
  Descriptive = 2
}

export type Question = {
  id: number;
  type: number;
  page: number;
  description: string;
  attemptLimit: number;
  alternatives: Alternative[];
  status: QuestionStatus;
  imageURL: string;
};

export type Alternative = {
  id: number;
  description: string;
  isCorrect: boolean;
};

export type QuestionStatus = {
  isCorrect: boolean | null;
  attempts: number;
  alternativeAnsweredID: number | null;
  correctAlternativeID: number | null;
  answer: string;
};

/** secondsToCustomFormat format the time in seconds to the specified format
 * @example
 * secondsToCustomFormat(120, "hh:mm:ss") -> "00:02:00" */
export function secondsToCustomFormat(seconds: number, format: string): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formatParts = format.split(":");
  const formattedTimeParts: string[] = [];

  for (const part of formatParts) {
    if (part.includes("hh") || part.includes("h")) {
      formattedTimeParts.push(hours.toString().padStart(part.length, "0"));
    } else if (part.includes("mm") || part.includes("m")) {
      formattedTimeParts.push(minutes.toString().padStart(part.length, "0"));
    } else if (part.includes("ss") || part.includes("s")) {
      formattedTimeParts.push(
        remainingSeconds.toString().padStart(part.length, "0")
      );
    } else {
      formattedTimeParts.push(part);
    }
  }

  return formattedTimeParts.join(":");
}
